import React, { useState, useEffect } from "react";
import {
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Button,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SecurityIcon from "@mui/icons-material/Security";
import SmsIcon from "@mui/icons-material/Sms";
import EmailIcon from "@mui/icons-material/Email";
import { toast } from "react-toastify";
import { observer } from "mobx-react-lite";
import useUserData from "../custom-hooks/useUserData";
import authStore from "../../stores/AuthStore";

const OptionComponent = observer(() => {
  const currentUser = authStore.getUser();
  console.log("User in option is:", currentUser);
  const userID = currentUser.userID;
  console.log("User id is:", userID);

  const { updateUserPreferenceData } = useUserData(userID);
  const userPreference = authStore.getPreference();
  console.log("User preference is:", userPreference);

  const defaultPreference = {
    theme: "Light",
    language: "English",
    timeZone: "GMT",
    dateFormat: "MM/DD/YYYY",
    mfa: false,
    mfaType: "None",
    mfaChoice: "",
  };

  const [formData, setFormData] = useState({
    ...defaultPreference,
    ...userPreference,
  });

  const [mfaTypeSubmitted, setMfaTypeSubmitted] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, ...userPreference }));
  }, [userPreference]);

  console.log("Initial form data is: ", formData);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    if (name === "mfa") {
      if (!newValue) {
        setFormData({
          ...formData,
          mfa: newValue,
          mfaType: "None",
          mfaChoice: "",
        });
        setMfaTypeSubmitted(false);
      } else {
        setFormData({ ...formData, mfa: newValue });
      }
    } else {
      setFormData({ ...formData, [name]: newValue });
    }

    if (name === "mfaType" && value !== "None") {
      setMfaTypeSubmitted(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (formData.mfa && formData.mfaType === "None") {
      setMfaTypeSubmitted(true);
      return;
    }

    const updatedMfaChoice = formData.mfa
      ? formData.mfaChoice || "Everytime"
      : "";
    try {
      const payload = {
        userID,
        ...formData,
        mfaChoice: updatedMfaChoice,
      };

      console.log("Sending payload: ", payload);

      const result = await updateUserPreferenceData.mutateAsync(payload);
      toast.success("Preference updated successfully!");
      authStore.setPreference(result);
      setFormData({
        ...defaultPreference,
        ...result,
      });

      setMfaTypeSubmitted(false);
    } catch (error) {
      console.error("Error while updating preference!", error);
      toast.error("Error while updating preference!");
    }
  };

  return (
    <Paper
      elevation={3}
      className="p-6 bg-white !rounded-2xl shadow-md mx-auto space-y-8 relative"
    >
      <form onSubmit={handleUpdate} className="space-y-6">
        {/* Theme */}
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="theme-label">
            <span className="flex items-center">
              <LanguageIcon className="mr-1" />
              Theme
            </span>
          </InputLabel>
          <Select
            labelId="theme-label"
            name="theme"
            value={
              formData.theme?.charAt(0).toUpperCase() +
              formData.theme?.slice(1).toLowerCase()
            }
            onChange={handleChange}
            label="Theme (...)"
          >
            <MenuItem value="System">System-Default</MenuItem>
            <MenuItem value="Light">Light</MenuItem>
            <MenuItem value="Dark">Dark</MenuItem>
          </Select>
        </FormControl>

        {/* Language */}
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="language-label">
            <span className="flex items-center">
              <LanguageIcon className="mr-1" />
              Language
            </span>
          </InputLabel>
          <Select
            labelId="language-label"
            name="language"
            value={formData.language}
            onChange={handleChange}
            label="Language (...)"
          >
            <MenuItem value="English">English</MenuItem>
            <MenuItem value="Spanish">Spanish</MenuItem>
            <MenuItem value="French">French</MenuItem>
            <MenuItem value="German">German</MenuItem>
          </Select>
        </FormControl>

        {/* Time Zone */}
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="timezone-label">
            <span className="flex items-center">
              <AccessTimeIcon className="mr-1" />
              Time Zone
            </span>
          </InputLabel>
          <Select
            labelId="timezone-label"
            name="timeZone"
            value={formData.timeZone}
            onChange={handleChange}
            label="Time Zone (...)"
          >
            <MenuItem value="GMT">GMT</MenuItem>
            <MenuItem value="UTC">UTC</MenuItem>
            <MenuItem value="EST">EST</MenuItem>
            <MenuItem value="CST">CST</MenuItem>
            <MenuItem value="PST">PST</MenuItem>
          </Select>
        </FormControl>

        {/* Date Format */}
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="dateformat-label">
            <span className="flex items-center">
              <CalendarTodayIcon className="mr-1" />
              Date Format
            </span>
          </InputLabel>
          <Select
            labelId="dateformat-label"
            name="dateFormat"
            value={formData.dateFormat}
            onChange={handleChange}
            label="Date Format (...)"
          >
            <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
            <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
            <MenuItem value="YYYY/MM/DD">YYYY/MM/DD</MenuItem>
          </Select>
        </FormControl>

        {/* MFA Toggle */}
        <Box className="flex items-center">
          <Box className="flex items-center">
            <SecurityIcon />
            <Typography variant="subtitle1" className="ml-1">
              MFA
            </Typography>
          </Box>
          <Switch
            name="mfa"
            checked={formData.mfa}
            onChange={handleChange}
            className="[&.Mui-checked]:text-green-500 [&.Mui-checked+ .MuiSwitch-track]:bg-green-500"
          />
        </Box>

        {/* MFA Type */}
        {formData.mfa && (
          <FormControl
            variant="outlined"
            fullWidth
            error={mfaTypeSubmitted && formData.mfaType === "None"}
          >
            <InputLabel id="mfatype-label" shrink>
              <span className="flex items-center">
                <SmsIcon className="mr-1" />
                MFA Type
                <span style={{ opacity: 0 }}> extra</span>
              </span>
            </InputLabel>
            <Select
              labelId="mfatype-label"
              name="mfaType"
              value={formData.mfaType}
              onChange={handleChange}
              label="MFA Type (...)"
            >
              <MenuItem value="None" className="!normal-case">
                None
              </MenuItem>
              <MenuItem value="TOTP">TOTP (Authenticator App)</MenuItem>
              <MenuItem value="SMS">
                <span className="flex items-center">
                  <SmsIcon className="mr-1" />
                  SMS
                </span>
              </MenuItem>
              <MenuItem value="Email">
                <span className="flex items-center">
                  <EmailIcon className="mr-1" />
                  Email
                </span>
              </MenuItem>
            </Select>
            {mfaTypeSubmitted && formData.mfaType === "None" && (
              <Typography variant="caption" color="error">
                You must select a valid MFA type
              </Typography>
            )}
          </FormControl>
        )}

        {/* MFA Choice Field */}
        {formData.mfa && formData.mfaType !== "None" && (
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="choice-label" shrink>
              <span className="flex items-center">
                <AccessTimeIcon className="mr-1" />
                MFA Choice
              </span>
            </InputLabel>
            <Select
              labelId="choice-label"
              name="mfaChoice"
              value={formData.mfaChoice || "Everytime"}
              onChange={handleChange}
              label="MFA Choice (...)"
            >
              <MenuItem value="Everytime">Everytime</MenuItem>
              <MenuItem value="Occasional">Occasional</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Update Preferences Button */}
        <Box className="flex justify-end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className="!bg-green-500 hover:!bg-green-600 !normal-case"
          >
            Update Preferences
          </Button>
        </Box>
      </form>
    </Paper>
  );
});

export default OptionComponent;
